<template>
  <div class="custom-select">
    <btn
      class="custom-select__active"
      block-class="custom-select"
      modifiers="block"
      :label="activeText"
      :tabindex="tabIndex"
      type="button"
      @click="open = !open"
    >
      <template #icon>
        <icon-caret-down />
      </template>
    </btn>

    <Transition
      name="fade-down"
      @after-enter="setState(true)"
      @after-leave="setState(false)"
    >
      <ul
        v-if="open"
        ref="optionsWrapper"
        class="custom-select__options"
      >
        <li
          v-for="(option, index) of options"
          :key="index"
          class="custom-select__item"
        >
          <btn
            class="custom-select__option"
            block-class="custom-select"
            :label="option.title"
            :show-icon="false"
            type="button"
            :disabled="option.disabled && !notifyBis ? true : null"
            @click="update(option)"
          />

          <span
            v-if="option.caption"
            class="custom-select__caption"
            v-text="option.caption"
          />
        </li>
      </ul>
    </Transition>

    <select
      v-show="false"
      tabindex="-1"
      :name="name ? name : null"
    >
      <option
        v-for="(option, index) of options"
        ref="options"
        :key="index"
        :selected="option.value === value ? true : false"
        :value="option.value"
        v-text="option.title"
      />
    </select>
  </div>
</template>

<script>

/**
 * Vue: Custom select (custom-select)
 * -----------------------------------------------------------------------------
 * Select input with custom UI.
 * @param {Number} bodyClick - Unique timestamp values to detect body clicks.
 * @param {Object} options - value/label options for select interface
 * @param {String} tabIndex - value for tabindex
 * @param {String} name - name for select element
 * @param {String} placeholder - placeholder value
 * @param {Number, String} value - default value to override placeholder
 *
 */
import { mapActions } from 'vuex'
import Btn from '~global/btn/btn'
import IconCaretDown from '~icons/direction/caret/down.svg'

export default {
  name: 'CustomSelect',

  components: {
    Btn,
    IconCaretDown,
  },

  props: {
    bodyClick: {
      type: Number,
      default: 0,
    },
    options: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    notifyBis: {
      type: Boolean,
      default: false,
      required: true,
    },
    tabIndex: {
      type: String,
      default: '0',
    },
    name: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    value: {
      type: [Number, String],
      default: '',
    },
  },

  emits: {
    update: (payload) => typeof payload === 'object',
    'state-change': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      selected: false,
      open: false,
      state: false,
      height: 0,
    }
  },

  computed: {

    /**
     * Compute text for active option.
     * @returns {String}
     */
    activeText() {
      if (this.selected) {
        return this.selected
      }

      if (this.value) {
        const found = this.options.find((option) => option.value === this.value)

        if (found) {
          return found.title
        }
      }

      return this.placeholder
    },
  },

  watch: {

    /**
     * Watch for body click to close custom select.
     */
    bodyClick() {
      this.handleBodyClick()
    },
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      toggleOverlay: 'overlays/toggle',
    }),

    /**
     * Open Modal.
     */
    openModal() {
      this.toggleOverlay({
        ignoreDismissed: true,
        namespace: 'backInStock',
      })
    },

    /**
     * Close open custom select on outer form body click.
     */
    handleBodyClick() {
      if (!this.open) {
        return
      }

      this.open = false
    },

    /**
     * Update select with chosen option.
     * @param {Object} option
     */
    update(option) {
      const found = this.$refs.options.find((opt) => opt.value === option.value.toString())

      if (!found) {
        return
      }

      this.open = false
      this.selected = option.title
      this.$emit('update', option.value)

      /**
       * Open BIS modal in case variant in not available
       * and BIS enabled for product.
       */
      if (option.disabled && this.notifyBis) {
        this.openModal()
      }
    },

    /**
     * Track and emit state of custom select.
     * @param {Boolean} state - Boolean to describe state of dropdown.
     */
    setState(state) {
      this.state = state

      this.$emit('state-change', {
        state: this.state,
        position: this.state ? this.$refs.optionsWrapper.getBoundingClientRect() : null,
      })
    },
  },
}
</script>
